import { DayjsFormat, getVendorIds, getVendors } from '@/helpers';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { getEventsCalendarData, getFilteredEvents } from './helpers';
import { EventsByDay, EventTypeFilter } from './types';
import { getCalendarEvents } from '@/api';
import { calculateDateBoundary } from './helpers';
import { useMantineTheme } from '@mantine/core';
import { eventTypeFilters } from './config';

export const useCalendarEvents = () => {
  const [currentDate, setCurrentDate] = useState<string>(dayjs().format(DayjsFormat.CALENDAR_DATE));
  const theme = useMantineTheme();
  const vendorIds = getVendorIds();
  const vendors = getVendors();
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format(DayjsFormat.CALENDAR_DATE)
  );
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>(vendorIds);
  const [selectedEventType, setSelectedEventType] = useState(eventTypeFilters);
  const today = useMemo(() => dayjs().startOf('day').format(DayjsFormat.CALENDAR_DATE), []);
  const minDate = useMemo(
    () => dayjs().subtract(3, 'month').startOf('month').format(DayjsFormat.CALENDAR_DATE),
    []
  );
  const maxDate = useMemo(() => dayjs().add(2, 'week').format(DayjsFormat.CALENDAR_DATE), []);

  const startTimeAfter = useMemo(
    () => calculateDateBoundary(currentDate, today, 'startOf', minDate, 'before', 'subtract'),
    [currentDate, today, minDate]
  );

  const startTimeBefore = useMemo(() => {
    if (!dayjs(currentDate).isBefore(today, 'day')) {
      return dayjs(maxDate).endOf('day').format();
    }
    return calculateDateBoundary(currentDate, today, 'endOf', maxDate, 'after', 'add');
  }, [currentDate, today, maxDate]);

  const {
    data: calendarEventsData,
    loading: isLoading,
    networkStatus,
  } = useQuery(getCalendarEvents, {
    variables: {
      vendorIds,
      startTimeBefore,
      startTimeAfter,
    },
    skip: vendorIds.length === 0,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const events = calendarEventsData?.searchEventVendors?.eventVendors?.nodes || [];
  const filteredEvents = getFilteredEvents(events, selectedVendorIds, selectedEventType);

  const calendarEvents: EventsByDay = getEventsCalendarData({
    events: filteredEvents,
    startDate: startTimeAfter,
    endDate: startTimeBefore,
    theme,
  });

  const handleSelectDate = (date: string) => {
    if (date !== selectedDate) setSelectedDate(date);
  };

  const handleEventTypeChange = (events: EventTypeFilter[]) => {
    if (events.every((event) => !event.value)) {
      return;
    }
    setSelectedEventType(events);
  };

  const isRefetching = networkStatus === 4;
  return {
    calendarEvents,
    maxDate,
    minDate,
    currentDate,
    handleDateChange: setCurrentDate,
    isLoading,
    isRefetching,
    onSelectVendors: setSelectedVendorIds,
    onSelectEventType: handleEventTypeChange,
    selectedEventType,
    vendors,
    selectedDate,
    handleSelectDate,
    selectedVendorIds,
  };
};
